/**
 * JSON-LD Schema helpers for Technical SEO
 */

import { siteConfig } from '../config';

const siteUrl = siteConfig.siteUrl;
const siteName = siteConfig.siteName;

export interface Game {
  slug: string;
  title: string;
  description: string;
  thumbnail: string;
  playUrl: string;
  developer?: string;
  rating?: string;
  votes?: string;
  released?: string;
  categories: string[];
}

/**
 * Organization & WebSite Schema for Homepage
 */
export function buildHomePageSchema() {
  const organization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteName,
    url: siteUrl,
    logo: {
      '@type': 'ImageObject',
      url: `${siteUrl}/assets/img/logo.png`,
    },
    sameAs: [
      // Add social media profiles here if available
    ],
  };

  const website = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteName,
    url: siteUrl,
    inLanguage: 'en',
    description: 'Play the best free online games instantly in your browser. No downloads required!',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [organization, website],
  };
}

/**
 * VideoGame Schema for Game Pages
 * Enhanced with keywords and more detailed properties
 */
export function buildGameSchema(game: Game) {
  const aggregateRating = game.rating && game.votes ? {
    '@type': 'AggregateRating',
    ratingValue: game.rating,
    ratingCount: game.votes.replace(/,/g, ''),
    bestRating: '5',
    worstRating: '1',
    reviewCount: game.votes.replace(/,/g, ''),
  } : undefined;

  const currentDate = new Date().toISOString();
  
  // Extract keywords from game title and categories
  const keywords = [
    game.title.toLowerCase(),
    ...game.categories,
    'free online game',
    'browser game',
    'unblocked game',
    'no download'
  ];

  return {
    '@context': 'https://schema.org',
    '@type': 'VideoGame',
    name: game.title,
    alternateName: game.slug === 'drift-hunters' ? 'Drift Hunters Unblocked' : undefined,
    description: game.description,
    url: `${siteUrl}/${game.slug}`,
    image: `${siteUrl}${game.thumbnail}`,
    screenshot: `${siteUrl}${game.thumbnail}`,
    genre: game.categories,
    keywords: keywords.join(', '),
    inLanguage: ['en', 'es', 'fr', 'ja', 'pt', 'zh'],
    isAccessibleForFree: true,
    author: game.developer ? {
      '@type': 'Organization',
      name: game.developer,
    } : undefined,
    publisher: {
      '@type': 'Organization',
      name: siteName,
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/assets/img/logo.png`,
      }
    },
    datePublished: game.released,
    dateModified: currentDate,
    applicationCategory: 'Game',
    operatingSystem: 'Any (Web Browser)',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      priceValidUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    },
    aggregateRating,
    gamePlatform: ['Web Browser', 'Desktop', 'Mobile', 'Tablet'],
    playMode: 'SinglePlayer',
    interactionStatistic: aggregateRating ? {
      '@type': 'InteractionCounter',
      interactionType: 'https://schema.org/ReviewAction',
      userInteractionCount: game.votes?.replace(/,/g, ''),
    } : undefined,
  };
}

/**
 * BreadcrumbList Schema for Game Pages
 */
export function buildGameBreadcrumbSchema(game: Game, categoryName?: string, categorySlug?: string) {
  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: siteUrl,
    },
  ];

  if (categoryName && categorySlug) {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: categoryName,
      item: `${siteUrl}/category/${categorySlug}`,
    });
    items.push({
      '@type': 'ListItem',
      position: 3,
      name: game.title,
      item: `${siteUrl}/${game.slug}`,
    });
  } else {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: game.title,
      item: `${siteUrl}/${game.slug}`,
    });
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

/**
 * CollectionPage Schema for Category Pages
 */
export function buildCategorySchema(categoryName: string, categorySlug: string, description: string, gamesCount: number) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} Games`,
    description,
    url: `${siteUrl}/category/${categorySlug}`,
    mainEntity: {
      '@type': 'ItemList',
      name: `${categoryName} Games`,
      description,
      numberOfItems: gamesCount,
    },
  };
}

/**
 * BreadcrumbList Schema for Category Pages
 */
export function buildCategoryBreadcrumbSchema(categoryName: string, categorySlug: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryName,
        item: `${siteUrl}/category/${categorySlug}`,
      },
    ],
  };
}

/**
 * FAQPage Schema for Games with FAQs
 */
export interface FAQItem {
  question: string;
  answer: string;
}

export function buildFAQSchema(faqs: FAQItem[]) {
  if (!faqs || faqs.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

/**
 * Combine multiple schemas into a @graph
 */
export function combineSchemas(...schemas: any[]) {
  return {
    '@context': 'https://schema.org',
    '@graph': schemas,
  };
}
